import { useEffect, type RefObject } from 'react';

const FOCALISABLES =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Piège le focus dans `conteneur` tant que `actif` : focus sur le premier
 * élément à l'ouverture, Tab et Maj+Tab bouclent, et le focus revient à
 * l'élément qui l'avait à la fermeture.
 */
export function usePiegeFocus(conteneur: RefObject<HTMLElement | null>, actif: boolean): void {
  useEffect(() => {
    if (!actif) return;
    const racine = conteneur.current;
    if (!racine) return;
    const precedent = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const elements = (): HTMLElement[] =>
      Array.from(racine.querySelectorAll<HTMLElement>(FOCALISABLES));
    elements()[0]?.focus();

    const surTouche = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') return;
      const liste = elements();
      if (liste.length === 0) return;
      const premier = liste[0]!;
      const dernier = liste[liste.length - 1]!;
      if (event.shiftKey && document.activeElement === premier) {
        event.preventDefault();
        dernier.focus();
      } else if (!event.shiftKey && document.activeElement === dernier) {
        event.preventDefault();
        premier.focus();
      }
    };
    racine.addEventListener('keydown', surTouche);
    return () => {
      racine.removeEventListener('keydown', surTouche);
      precedent?.focus();
    };
  }, [conteneur, actif]);
}
